// Regras puras da tela de convites (croqui `screens/admin/convites.tsx`): rótulo e tom
// de cada status, abas, busca client-side e o corpo do `POST /admin/v1/invites`.
//
// Fora dos componentes porque é o que o teste unitário afirma sem montar árvore.

import type {
  AdminInvite,
  AdminInviteStatus,
  CreateInviteBody,
} from '../../api/queries/admin-invites.ts';
import type { PillTone } from '../../components/kit/pill.tsx';
import { formatShortDate, formatTime } from '../../lib/dates.ts';

/**
 * O croqui chamava o status de `pending`; o enum real da API é `active` (marca #114 no
 * croqui, PATCH rev 2-6). O VALOR é o da API; o RÓTULO continua o do croqui, que é o
 * mesmo vocabulário do contador `invites_pending` de `GET /admin/v1/counters`.
 */
const META: Record<AdminInviteStatus, { label: string; tone: PillTone }> = {
  active: { label: 'Pendente', tone: 'wait' },
  accepted: { label: 'Aceito', tone: 'ok' },
  expired: { label: 'Expirado', tone: 'neutral' },
  revoked: { label: 'Revogado', tone: 'bad' },
};

export function inviteStatusPill(status: AdminInviteStatus): { label: string; tone: PillTone } {
  return META[status];
}

/** `all` é valor de aba, não ausência de filtro: "Todos" precisa ser representável. */
export const TAB_IDS = ['all', 'active', 'accepted', 'expired', 'revoked'] as const;

export type TabId = (typeof TAB_IDS)[number];

export const TAB_LABEL: Record<TabId, string> = {
  all: 'Todos',
  active: 'Pendentes',
  accepted: 'Aceitos',
  expired: 'Expirados',
  revoked: 'Revogados',
};

/**
 * Só o convite pendente tem ação na linha. Reenviar é criar outro (a API revoga o ativo
 * do mesmo email) e revogar mata o link; aceito, expirado e revogado não têm o que
 * reenviar dali, para esses o caminho é "Convidar seller".
 */
export function canAct(status: AdminInviteStatus): boolean {
  return status === 'active';
}

/**
 * Busca por email/loja NAS LINHAS JÁ CARREGADAS: `GET /admin/v1/invites` não aceita
 * `q` (marca #117). A tela diz quantas linhas foram consideradas em vez de fingir que
 * varreu o banco.
 */
export function matchesSearch(invite: AdminInvite, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (needle === '') {
    return true;
  }
  return (
    invite.email.toLowerCase().includes(needle) ||
    (invite.store_name ?? '').toLowerCase().includes(needle)
  );
}

/** `20/09 09:12`, o carimbo curto do croqui; convite vive no máximo 30 dias. */
export function shortStamp(value: string): string {
  return `${formatShortDate(value)} ${formatTime(value)}`;
}

/** Validade oferecida no drawer. A API aceita 1 a 30 (`spec/03` §4.8). */
export const EXPIRY_DAYS = [1, 3, 7, 14, 30] as const;

export const DEFAULT_EXPIRY_DAYS = 7;

export interface NewInviteValues {
  email: string;
  store_name: string;
  expires_in_days: number;
}

/**
 * `store_name` vazio SAI do corpo: a API exige 1-200 caracteres quando o campo vem, e
 * `''` viraria `validation_failed` no campo que o drawer anuncia como opcional.
 */
export function inviteBodyOf(values: NewInviteValues): CreateInviteBody {
  const store = values.store_name.trim();
  return {
    email: values.email.trim(),
    ...(store === '' ? {} : { store_name: store }),
    expires_in_days: values.expires_in_days,
  };
}
